import { trackEvent } from '@pm/analytics';
import { Text, TextLink } from '@pm/ui';
import { Trans, useTranslation } from 'react-i18next';

const USPS_TRACKING_URL = 'https://tools.usps.com/go/TrackConfirmAction';
interface LabKitShipmentDetailsProps {
  status: string;
  description: string;
  trackingNumber: string;
  supportLink: string;
  shouldShowSupportLink?: boolean;
}

export const LabKitShipmentDetails = ({
  status,
  description,
  trackingNumber,
  supportLink,
  shouldShowSupportLink = false,
}: LabKitShipmentDetailsProps) => {
  const { t } = useTranslation(['labs', 'shipment']);

  const getTrackingUrl = (trackingNumber: string) => {
    return `${USPS_TRACKING_URL}?tLabels=${trackingNumber}`;
  };

  return (
    <div className="pb-xs">
      {description && (
        <Description
          shouldShowSupportLink={shouldShowSupportLink}
          description={description}
          supportLink={supportLink}
        />
      )}
      {(status || trackingNumber) && (
        <dl>
          {status && (
            <div>
              <Text size="label" as="dt">
                {t('shipment:LabKit.Status')}
              </Text>
              <Text size="labelStrong" as="dd">
                {status}
              </Text>
            </div>
          )}
          {trackingNumber && (
            <div className="pt-s">
              <Text size="label" as="dt">
                {t('shipment:LabKit.USPSTrackingNumber')}
              </Text>
              <Text size="labelStrong" as="dd">
                {trackingNumber}
              </Text>
            </div>
          )}
        </dl>
      )}
      {trackingNumber && (
        <div className="pt-l">
          <Text size="bodyL">
            <Trans ns="shipment" i18nKey="LabKit.TrackingInformation">
              <TextLink
                to={
                  trackingNumber
                    ? getTrackingUrl(trackingNumber)
                    : USPS_TRACKING_URL
                }
                external
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackEvent('Link Clicked', {
                    linkText: t('shipment:LabKit.USPSTrackingSite'),
                    linkName: 'usps_tracking_site',
                    currentPath: window.location.pathname,
                    currentPageName: 'Lab Work',
                    targetPath: trackingNumber
                      ? getTrackingUrl(trackingNumber)
                      : USPS_TRACKING_URL,
                  });
                }}
              />
            </Trans>
          </Text>
        </div>
      )}
    </div>
  );
};

const PlainDescription = ({ description }: { description: string }) => (
  <Text size="bodyL">{description}</Text>
);

const SupportDescription = ({ supportLink }: { supportLink: string }) => {
  const { t } = useTranslation(['shipment']);

  return (
    <Text size="bodyL">
      <Trans
        ns="shipment"
        i18nKey="LabKit.ShippingStatus.USPSTrackingSiteWithSupport"
      >
        <TextLink
          to={supportLink}
          external
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            trackEvent('Link Clicked', {
              linkText: t('LabKit.SupportLinkText'),
              linkName: 'contact_our_support_team',
              currentPath: window.location.pathname,
              currentPageName: 'Lab Work',
              targetPath: supportLink,
            });
          }}
        />
      </Trans>
    </Text>
  );
};

const Description = ({
  shouldShowSupportLink,
  description,
  supportLink,
}: {
  shouldShowSupportLink: boolean;
  description: string;
  supportLink: string;
}) => {
  return (
    <div className="pt-xs pb-l">
      {shouldShowSupportLink ? (
        <SupportDescription supportLink={supportLink} />
      ) : (
        <PlainDescription description={description} />
      )}
    </div>
  );
};
